import { Tooltip } from "@xola/ui-kit";
import { Form, FormikErrors, FormikProps } from "formik";
import { isEmpty, startCase, uniqueId } from "lodash";
import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { GiftOffer } from "types";
import { object } from "yup";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { CheckoutButton } from "./CheckoutButton";
import { CreatePurchaseFooter } from "./CreatePurchaseFooter";
import { CreatePurchaseGiftFields } from "./CreatePurchaseGiftFields";
import { CreatePurchaseSidebar } from "./CreatePurchaseSidebar";
import { getPurchaseItemsHavingId } from "./helpers/PurchaseCreateHelpers";
import { useGiftFieldsValidations } from "./hooks/useCheckoutPurchaseScreenValidations";
import { GiftFormValues, useCreatePurchaseGiftInitialValues } from "./hooks/useCreatePurchaseGiftInitialValues";

interface CreatePurchaseGiftScreenProps {
    product: GiftOffer;
    onSubmit: () => void;
}

/**
 * Note:
 *
 * `<CreatePurchaseGiftFields />` contains all the fields required for gift item purchase, and it manages own field states with Formik context rather than directly relying on `createPurchaseStore`,
 * So, in order to reuse that component we are creating a <XolaFormik /> form for validations and other stuff, rather than managing it with `disabledReasons` as in CreateMistPurchaseItemScreen.
 */
export const CreatePurchaseGiftScreen = ({ product, onSubmit }: CreatePurchaseGiftScreenProps) => {
    const history = useHistory();
    const formikRef = useRef<FormikProps<GiftFormValues>>(null);

    const initialValues = useCreatePurchaseGiftInitialValues();
    const validationSchema = useGiftFieldsValidations();

    const resetState = useCreatePurchaseStore((state) => state.actions.resetState);
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);
    const step = useCreatePurchaseStore((state) => state.step);

    /** Validates the form and displays the error if any */
    const validateForm = async (): Promise<boolean> => {
        const errors = await formikRef.current!.validateForm();

        if (!isEmpty(errors)) {
            // When we have errors in the form, and we call formik `handleSubmit` it would display the error messages on respective fields
            formikRef.current!.handleSubmit();
            return false;
        }

        return true;
    };

    const handleCancel = () => {
        resetState();

        history.push("/cart/init");
    };

    const handleAddNewProductClick = async () => {
        if (!(await validateForm())) return;
        setPurchase((draft) => getPurchaseItemsHavingId(draft));

        history.push("/cart/init", { isAddingItem: true });
    };

    const handleCheckout = (): any => {
        onSubmit();
    };

    const popoverParentId = uniqueId("popoverParentId");

    useEffect(() => {
        if (step === CreatePurchaseStep.GIFT_DETAILS_INVALID && formikRef.current) {
            /** When user visits the page with step `GIFT_DETAILS_INVALID`, we need to validate the form on initial render */
            void validateForm();
        }
    }, [step]);

    return (
        <div className="grid h-full grid-cols-12" id={popoverParentId}>
            <ContentLayout isWide classNames={{ root: "relative col-span-6 h-full 2xl:col-span-7" }}>
                <XolaFormik<GiftFormValues>
                    enableReinitialize
                    initialValues={initialValues as GiftFormValues}
                    innerRef={formikRef}
                    validationSchema={object().shape(validationSchema)}
                    onSubmit={handleCheckout}
                >
                    {({ errors, handleSubmit }) => (
                        <Form onSubmit={handleSubmit}>
                            <div className="space-y-4">
                                <CreatePurchaseGiftFields product={product} />
                            </div>

                            <CreatePurchaseFooter secondaryText="Cancel" onCancel={handleCancel}>
                                {Object.keys(errors).length > 0 ? (
                                    <Tooltip
                                        content={getErrorMessages(errors).map((message, index) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <div key={index}>{message}</div>
                                        ))}
                                    >
                                        <CheckoutButton disabled />
                                    </Tooltip>
                                ) : (
                                    <CheckoutButton type="submit" />
                                )}

                                <SecondaryButton onClick={handleAddNewProductClick}>
                                    Add Another Product
                                </SecondaryButton>
                            </CreatePurchaseFooter>
                        </Form>
                    )}
                </XolaFormik>
            </ContentLayout>

            <div className="col-span-6 shrink-0 space-y-6 bg-gray-lighter 2xl:col-span-5">
                <CreatePurchaseSidebar />
            </div>
        </div>
    );
};

/**
 * Merges field labels with error messages for formik errors
 *
 * @example
 * errors = {
 *     recipientEmail: 'is required'
 * }
 *
 * returns,
 * ["Recipient Email is required"]
 */
const getErrorMessages = (errors: FormikErrors<GiftFormValues>) => {
    return Object.entries(errors).map(([field, message]) => {
        return `${startCase(field)} ${message}`;
    });
};
